import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import HeadsetIcon from '../../images/icons/headphones.svg';
import EmailIcon from '../../images/icons/mail.svg';
import { HELP_SUBMENU } from '../../stores/reducers/session';
import { GreenroomState as GRS } from '../../stores/types/types';
import Popover from '../general-components/popover';
import HelpMenu from './help-menu';
import KeyboardListEntry, { KeyboardKey } from './keyboard-list-entry';

// This could maybe be exported from somewhere else if needed in other places
enum EUserType {
	Producer,
	Presenter,
	Viewer
}

interface HelpProps {
	isOpen: boolean;
	onClose: () => void;
}

export default function Help(props: HelpProps) {
	const { isOpen, onClose } = props;
	const activeHelpSubmenu = useSelector((s: GRS) => s.SessionReducer.activeHelpSubmenu);
	const host = useSelector((s: GRS) => s.SessionReducer.host);
	const watcherOnly = useSelector((s: GRS) => s.SessionReducer.watcher_only);

	// Could just rely on host if presenters and viewers are the same,
	// but this seems to make the code easier to understand
	const userType = useMemo(() => {
		if (host) {
			return EUserType.Producer;
		} if (!host && !watcherOnly) {
			return EUserType.Presenter;
		}
		return EUserType.Viewer;
	}, [host, watcherOnly]);

	const shortcutsMap = new Map<string, JSX.Element>([
		['producer-comms-all', (
			<KeyboardListEntry title="Producer Comms (all)">
				<KeyboardKey>Space</KeyboardKey>
			</KeyboardListEntry>
		)],
		['producer-comms-individual', (
			<KeyboardListEntry title="Producer Comms (Individual)">
				<KeyboardKey>Shift</KeyboardKey>+
				<KeyboardKey>Shot position #</KeyboardKey>
			</KeyboardListEntry>
		)],
		['clear-shot', (
			<KeyboardListEntry title="Clear Shot">
				<KeyboardKey>Backspace</KeyboardKey>/
				<KeyboardKey>Delete</KeyboardKey>
			</KeyboardListEntry>
		)],
		['change-tabs', (
			<KeyboardListEntry title="Change Tabs">
				<KeyboardKey>A</KeyboardKey>/
				<KeyboardKey>D</KeyboardKey>
			</KeyboardListEntry>
		)],
		['titles-firing', (
			<KeyboardListEntry title="Titles Firing (all)">
				<KeyboardKey>T</KeyboardKey>
			</KeyboardListEntry>
		)],
		['mute', (
			<KeyboardListEntry title="Mute">
				(presenters &amp; producers, mute yourself)
				<KeyboardKey>M</KeyboardKey>
			</KeyboardListEntry>
		)],
		['turn-off-video', (
			<KeyboardListEntry title="Turn off camera">
				(presenters &amp; producers, mute yourself)
				<KeyboardKey>V</KeyboardKey>
			</KeyboardListEntry>
		)],
		['slide-deck', (
			<KeyboardListEntry title="Slide Deck">
				<KeyboardKey>Left Arrow</KeyboardKey>/
				<KeyboardKey>Right Arrow</KeyboardKey>
			</KeyboardListEntry>
		)],
		['apply-layout-add-shot', (
			<KeyboardListEntry title="Apply Layout/Add Shot">
				<KeyboardKey>Enter</KeyboardKey>/
				<KeyboardKey>Return</KeyboardKey>
			</KeyboardListEntry>
		)],
		['play-pause-video-showbuilder', (
			<KeyboardListEntry title="Play/Pause Video (Showbuilder)">
				<KeyboardKey>Space</KeyboardKey>
			</KeyboardListEntry>
		)],
	]);

	const allShortcuts = useMemo(() => (
		Array.from(shortcutsMap).map(([key, value]) => (
			<React.Fragment key={key}>
				{value}
			</React.Fragment>
		))
	), [shortcutsMap]);

	let helpMenuContent: JSX.Element;

	switch (activeHelpSubmenu) {
		case HELP_SUBMENU.KEYBOARD_SHORTCUTS:
			helpMenuContent = (
				<div className="help-submenu shortcuts">
					<h4 className="help-submenu-title">Keyboard Shortcuts</h4>
					<div className="help-submenu-content">
						<h6>All shortcuts</h6>
						<dl>
							{userType === EUserType.Producer && allShortcuts}
							{(userType === EUserType.Presenter || userType === EUserType.Viewer) && (
								<>
									{shortcutsMap.get('change-tabs')}
									{shortcutsMap.get('mute')}
									{shortcutsMap.get('turn-off-video')}
									{shortcutsMap.get('slide-deck')}
									{shortcutsMap.get('play-pause-video-showbuilder')}
								</>
							)}
						</dl>
					</div>
				</div>
			);
			break;
		case HELP_SUBMENU.SUPPORT:
			helpMenuContent = (
				<div className="help-submenu support">
					<h4 className="help-submenu-title">Brandlive Support</h4>
					<div className="help-submenu-content">
						<dl className='support-list'>
							<dt>
								<div className='image-container'>
									<img src={EmailIcon} alt='email'/>
								</div>
								<span>support@brandlive.com</span>
							</dt>
							<dt>
								<div className='image-container'>
									<img src={HeadsetIcon} alt='headset'/>
								</div>
								<span>866 532 2154</span>
							</dt>
						</dl>
					</div>
				</div>
			);
			break;
		default:
			helpMenuContent = (
				<HelpMenu {...{ onClose }} />
			);
	}

	return (
		<Popover open={isOpen} close={onClose} className='help-wrapper'>
			{helpMenuContent}
		</Popover>
	);
}
